import analytics from "../../Services/analyticsService";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import MascotAvatar from "./MascotAvatar";
import useMyMascot from "./useMyMascot";
import { CAST, LETTER_WORDS } from "./mascotMood";
import "./mascot.css";

const GRAVITY = 2400; // px/s²
const FLOOR_BOUNCE = 0.45;
const WALL_BOUNCE = 0.6;
const MAX_SPEED = 2600;
const HOLD_MS = 450;
const DRAG_START_PX = 6;
const FRIEND_TAPS = 5;
const FRIEND_WINDOW_MS = 1500;
const FRIENDS_MS = 3200;
const REACTIONS = ["jump", "giggle", "spin", "word", "word"];
const LETTERS = Object.keys(LETTER_WORDS);

const pick = (list) => list[Math.floor(Math.random() * list.length)];
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const sizeFor = (mascot) => (mascot.base === "pencil" ? 44 : 64);

// Page elements the mascot can land on, and the spot it lives at when idle.
const SHELF_SELECTOR = "[data-mascot-shelf]";
const HOME_SELECTOR = "[data-mascot-home]";

function homeSpot() {
  const el = document.querySelector(HOME_SELECTOR);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top };
}

function shelves() {
  return [...document.querySelectorAll(SHELF_SELECTOR)].map((el) => el.getBoundingClientRect());
}

/** One of the other mascots popping in to say hi. Positions are viewport px. */
function Friend({ mascot, fromX, slotX, y, delay }) {
  const ref = useRef(null);
  const size = sizeFor(mascot);
  useEffect(() => {
    const el = ref.current;
    if (!el?.animate) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const dx = fromX - slotX;
    const enter = reduced
      ? [{ opacity: 0 }, { opacity: 1 }]
      : [
          { transform: `translate(${dx}px, 0)` },
          { transform: `translate(${dx / 2}px, -70px)`, offset: 0.5 },
          { transform: "translate(0, 0)" },
        ];
    const exit = reduced
      ? [{ opacity: 1 }, { opacity: 0 }]
      : [
          { transform: "translate(0, 0)" },
          { transform: `translate(${dx / 2}px, -70px)`, offset: 0.5 },
          { transform: `translate(${dx}px, 0)` },
        ];
    el.animate(enter, { duration: 650, delay, easing: "ease-out", fill: "backwards" });
    const hop = setTimeout(() => {
      if (!reduced) el.animate([{ transform: "none" }, { transform: "translateY(-22px)" }, { transform: "none" }], { duration: 420 });
    }, 650 + delay + 200);
    el.animate(exit, { duration: 600, delay: FRIENDS_MS - 700 + delay / 2, easing: "ease-in", fill: "forwards" });
    return () => clearTimeout(hop);
  }, [fromX, slotX, delay]);

  return (
    <div ref={ref} style={{ position: "fixed", left: slotX - size / 2, top: y, width: size, pointerEvents: "none" }}>
      <div style={{ transform: "translateY(-100%)" }}>
        <div className="absolute left-1/2 -translate-x-1/2 -top-7 whitespace-nowrap rounded-full bg-white border border-red-200 px-2 py-0.5 text-[11px] font-bold text-red-700 shadow-sm">
          Hi!
        </div>
        <MascotAvatar mascot={mascot} mood="celebrate" size={size} />
      </div>
    </div>
  );
}

/**
 * The player's mascot, floating over the page. It lives on the element marked
 * data-mascot-home and never moves layout. Tap for a reaction, tap five times
 * to bring its friends over, hold to squish, or drag and toss it: it bounces
 * off the screen edges, lands on anything marked data-mascot-shelf, then hops
 * home.
 */
export default function MascotPlayground() {
  const mascot = useMyMascot();
  const size = sizeFor(mascot);
  const bodyRef = useRef(null);
  const innerRef = useRef(null);
  const reduced = useRef(typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

  // Physics lives in refs so the animation loop never waits on React.
  // x = centre, y = feet, both in viewport px.
  const phys = useRef({ x: -999, y: -999, vx: 0, vy: 0, rot: 0, mode: "idle", t: 0 });
  const pointer = useRef(null);
  const taps = useRef([]);
  const faceTimer = useRef(0);
  const friendsTimer = useRef(0);

  const [mood, setMood] = useState("calm");
  const [letter, setLetter] = useState(null);
  const [bubble, setBubble] = useState(null);
  const [squished, setSquished] = useState(false);
  const [friends, setFriends] = useState(null);
  const [visible, setVisible] = useState(false);

  const setFaceFor = useCallback((m, ms, after = "calm") => {
    clearTimeout(faceTimer.current);
    setMood(m);
    if (ms) {
      faceTimer.current = setTimeout(() => {
        setMood(after);
        setLetter(null);
        setBubble(null);
      }, ms);
    }
  }, []);

  const paint = useCallback(() => {
    const p = phys.current;
    const el = bodyRef.current;
    if (!el) return;
    el.style.transform = `translate(${p.x - size / 2}px, ${p.y}px) translateY(-100%) rotate(${p.rot}deg)`;
  }, [size]);

  // One loop for everything: follow the home spot while idle (so scrolling and
  // layout shifts carry it along), and run physics otherwise.
  useEffect(() => {
    let raf = 0;
    const tick = (now) => {
      const p = phys.current;
      const dt = Math.min(0.032, (now - (p.t || now)) / 1000);
      p.t = now;
      const W = window.innerWidth;
      const H = window.innerHeight;
      const half = size / 2;
      const bodyH = bodyRef.current?.offsetHeight || size;

      if (p.mode === "idle") {
        const home = homeSpot();
        if (home) {
          p.x = home.x;
          p.y = home.y;
        }
        setVisible(!!home);
      } else if (p.mode === "fly") {
        const prevY = p.y;
        p.vy += GRAVITY * dt;
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.rot += p.vx * dt * 0.6;
        if (p.x < half || p.x > W - half) {
          p.x = clamp(p.x, half, W - half);
          p.vx = -p.vx * WALL_BOUNCE;
        }
        if (p.y - bodyH < 0) {
          p.y = bodyH;
          p.vy = Math.abs(p.vy) * WALL_BOUNCE;
        }
        let floor = H;
        if (p.vy > 0) {
          for (const r of shelves()) {
            if (prevY <= r.top + 1 && p.y >= r.top && p.x > r.left + 8 && p.x < r.right - 8 && r.top > bodyH) {
              floor = r.top;
              break;
            }
          }
        }
        if (p.y >= floor) {
          const impact = p.vy;
          p.y = floor;
          p.vy = -p.vy * FLOOR_BOUNCE;
          p.vx *= 0.8;
          if (impact > 1300) setFaceFor("dizzy");
          if (Math.abs(p.vy) < 90) {
            p.vy = 0;
            p.vx = 0;
            p.mode = "settle";
            p.settleAt = now;
          }
        }
      } else if (p.mode === "settle") {
        p.rot = ((((p.rot % 360) + 540) % 360) - 180) * (1 - Math.min(1, dt * 10));
        if (now - p.settleAt > 650) {
          const home = homeSpot() || { x: W / 2, y: H };
          p.rot = 0;
          p.mode = "hop";
          p.hop = { fromX: p.x, fromY: p.y, t0: now, dur: 750, h: 70 + Math.hypot(home.x - p.x, home.y - p.y) / 5 };
          setFaceFor("happy");
        }
      } else if (p.mode === "hop") {
        const home = homeSpot() || { x: W / 2, y: H };
        const k = Math.min(1, (now - p.hop.t0) / p.hop.dur);
        const ease = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
        p.x = p.hop.fromX + (home.x - p.hop.fromX) * ease;
        p.y = p.hop.fromY + (home.y - p.hop.fromY) * ease - Math.sin(Math.PI * k) * p.hop.h;
        p.rot = Math.sin(Math.PI * k * 2) * 8;
        if (k >= 1) {
          p.rot = 0;
          p.mode = "idle";
          setFaceFor("calm");
        }
      }

      if (p.mode !== "held") paint();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paint, setFaceFor, size]);

  useEffect(
    () => () => {
      clearTimeout(faceTimer.current);
      clearTimeout(friendsTimer.current);
      clearTimeout(pointer.current?.holdTimer);
    },
    []
  );

  const animateInner = (frames, opts) => {
    if (reduced.current || !innerRef.current?.animate) return;
    innerRef.current.animate(frames, { duration: 600, easing: "cubic-bezier(.3,.7,.4,1)", ...opts });
  };

  const callFriends = () => {
    analytics.track("mascot_interacted", { surface: "home", action: "friends" });
    const p = phys.current;
    const W = window.innerWidth;
    // The game's host of the other base, plus the three bots.
    const others = [mascot.base === "pencil" ? "letty" : "namey", "kafi", "kosi", "kemi"];
    // Two on each side when there's room; otherwise all four on the roomier side.
    const left = p.x - 30;
    const right = W - 30 - p.x;
    let offsets;
    if (Math.min(left, right) >= 2 * 70) {
      const gap = Math.min(78, Math.min(left, right) / 2);
      offsets = [-2 * gap, -gap, gap, 2 * gap];
    } else {
      const dir = right > left ? 1 : -1;
      const gap = Math.min(78, Math.max(left, right) / 4);
      offsets = [1, 2, 3, 4].map((n) => dir * n * gap);
    }
    setFriends(
      others.map((id, i) => ({
        id,
        slotX: clamp(p.x + offsets[i], 30, W - 30),
        y: p.y,
        fromX: offsets[i] < 0 ? -60 : W + 60,
        delay: i * 110,
      }))
    );
    setBubble("My friends!");
    setFaceFor("celebrate", FRIENDS_MS, "happy");
    clearTimeout(friendsTimer.current);
    friendsTimer.current = setTimeout(() => setFriends(null), FRIENDS_MS + 200);
  };

  const react = () => {
    analytics.track("mascot_interacted", { surface: "home", action: "tap" });
    const now = performance.now();
    if (friends) {
      animateInner([{ transform: "none" }, { transform: "translateY(-18px)" }, { transform: "none" }], { duration: 400 });
      return;
    }
    taps.current = [...taps.current.filter((t) => now - t < FRIEND_WINDOW_MS), now];
    if (taps.current.length >= FRIEND_TAPS) {
      taps.current = [];
      callFriends();
      return;
    }
    const kind = pick(REACTIONS);
    if (kind === "jump") {
      setFaceFor("celebrate", 900);
      animateInner([{ transform: "translateY(0) scale(1.08,.9)" }, { transform: "translateY(-46px) scale(.94,1.08)" }, { transform: "translateY(0) scale(1.1,.88)" }, { transform: "none" }], { duration: 700 });
    } else if (kind === "giggle") {
      setFaceFor("happy", 900);
      animateInner([{ transform: "rotate(0)" }, { transform: "rotate(-8deg)" }, { transform: "rotate(8deg)" }, { transform: "rotate(-6deg)" }, { transform: "rotate(6deg)" }, { transform: "rotate(0)" }], { duration: 600, easing: "linear" });
    } else if (kind === "spin") {
      setFaceFor("happy", 900);
      animateInner([{ transform: "rotate(0)" }, { transform: "rotate(360deg)" }], { duration: 650 });
    } else {
      const l = pick(LETTERS);
      if (mascot.base !== "pencil") setLetter(l);
      setBubble(`${l} is for ${LETTER_WORDS[l]}!`);
      setFaceFor("celebrate", 1600);
      animateInner([{ transform: "scale(1)" }, { transform: "scale(1.12)" }, { transform: "scale(1)" }], { duration: 400 });
    }
  };

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const p = phys.current;
    const prevMode = p.mode;
    p.mode = "held";
    pointer.current = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      grabX: e.clientX - p.x,
      grabY: e.clientY - p.y,
      dragging: false,
      squish: false,
      prevMode,
      samples: [{ x: e.clientX, y: e.clientY, t: performance.now() }],
      holdTimer: setTimeout(() => {
        if (!pointer.current || pointer.current.dragging) return;
        pointer.current.squish = true;
        setSquished(true);
        setFaceFor("squish");
      }, HOLD_MS),
    };
  };

  const onPointerMove = (e) => {
    const ptr = pointer.current;
    if (!ptr || ptr.id !== e.pointerId) return;
    const dx = e.clientX - ptr.startX;
    const dy = e.clientY - ptr.startY;
    if (!ptr.dragging && Math.hypot(dx, dy) > DRAG_START_PX && !ptr.squish) {
      ptr.dragging = true;
      clearTimeout(ptr.holdTimer);
      setFaceFor("scared");
    }
    if (!ptr.dragging) return;
    const now = performance.now();
    ptr.samples = [...ptr.samples.filter((s) => now - s.t < 90), { x: e.clientX, y: e.clientY, t: now }];
    const p = phys.current;
    p.x = clamp(e.clientX - ptr.grabX, size / 2, window.innerWidth - size / 2);
    p.y = clamp(e.clientY - ptr.grabY, size, window.innerHeight);
    p.rot = clamp(dx * 0.08, -20, 20);
    paint();
  };

  const onPointerUp = (e) => {
    const ptr = pointer.current;
    if (!ptr || ptr.id !== e.pointerId) return;
    clearTimeout(ptr.holdTimer);
    pointer.current = null;
    const p = phys.current;

    if (ptr.squish) {
      setSquished(false);
      setFaceFor("celebrate", 700);
      p.mode = ptr.prevMode === "idle" ? "idle" : "fly";
      return;
    }
    if (ptr.dragging) {
      const first = ptr.samples[0];
      const last = ptr.samples[ptr.samples.length - 1];
      const span = Math.max(16, last.t - first.t) / 1000;
      p.vx = clamp((last.x - first.x) / span, -MAX_SPEED, MAX_SPEED);
      p.vy = clamp((last.y - first.y) / span, -MAX_SPEED, MAX_SPEED);
      if (reduced.current) {
        p.rot = 0;
        p.mode = "idle";
        setFaceFor("calm");
        return;
      }
      p.mode = "fly";
      setFaceFor("whee");
      return;
    }
    // A plain tap: react, and keep doing whatever it was doing.
    p.mode = ptr.prevMode === "held" ? "idle" : ptr.prevMode;
    react();
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      react();
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <div aria-live="polite" style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 40, overflow: "hidden" }}>
      {friends?.map((f) => (
        <Friend key={f.id} mascot={CAST[f.id]} fromX={f.fromX} slotX={f.slotX} y={f.y} delay={f.delay} />
      ))}
      <div
        ref={bodyRef}
        role="button"
        tabIndex={0}
        aria-label="Play with the mascot: tap, hold, or drag and throw it"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
        className="cursor-grab active:cursor-grabbing outline-none focus-visible:ring-2 focus-visible:ring-red-400 rounded-2xl"
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          width: size,
          touchAction: "none",
          pointerEvents: "auto",
          visibility: visible ? "visible" : "hidden",
          transformOrigin: "50% 100%",
        }}
      >
        {bubble && (
          <div className="absolute left-1/2 -translate-x-1/2 -top-8 whitespace-nowrap rounded-full bg-white border border-red-200 px-3 py-1 text-xs font-bold text-red-700 shadow-sm pointer-events-none">
            {bubble}
          </div>
        )}
        <div
          ref={innerRef}
          style={{
            transformOrigin: "50% 100%",
            transform: squished ? "scale(1.18, .72)" : "none",
            transition: "transform .18s cubic-bezier(.3,1.6,.5,1)",
          }}
        >
          <MascotAvatar key={`${mascot.base}-${mascot.color}-${mascot.face}-${mood}-${letter || ""}`} mascot={mascot} mood={mood} letter={letter} size={size} shadow={false} />
        </div>
      </div>
    </div>,
    document.body
  );
}
